import { GitMerge } from 'lucide-react'

import { Button, Portal } from '@/shared/components/ui'
import { useI18n } from '@/shared/i18n'

import type { DocumentDetail } from '../types'

export interface ConflictDialogProps {
  /** Lo que hay en el editor de esta pestaña. */
  mine: string
  /** Lo que tiene el servidor ahora mismo. */
  theirs: DocumentDetail
  onKeepMine: () => void
  onDiscardMine: () => void
  saving?: boolean
}

/**
 * 409: alguien guardó antes.
 *
 * Se muestran **las dos versiones**, no un mensaje. "Alguien guardó antes que
 * vos" sin dejar ver qué escribió obliga a elegir a ciegas entre perder lo tuyo
 * y pisar lo del otro.
 */
export function ConflictDialog({
  mine,
  theirs,
  onKeepMine,
  onDiscardMine,
  saving = false,
}: ConflictDialogProps) {
  const { t, formatDateTime } = useI18n()
  // Quién guardó y cuándo van en UNA frase: el orden de "por X el Y" cambia de
  // idioma en idioma, así que no se arma pegando pedazos.
  const savedBy = t('docs.conflict.savedBy', {
    persona: theirs.updated_by?.display_name ?? t('common.label.unknown'),
    fecha: formatDateTime(theirs.updated_at),
  })

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-bg/70 p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('docs.conflict.label')}
          className="hairline flex max-h-full w-full max-w-[860px] flex-col rounded-[var(--radius-card)] bg-surface-1"
        >
          <header className="hairline-b flex items-start gap-2 px-4 py-3">
            <GitMerge size={16} aria-hidden="true" style={{ color: 'hsl(var(--due-soon))' }} />
            <div className="flex flex-col gap-0.5">
              <h2 className="text-base text-text-1">{t('docs.conflict.title')}</h2>
              <p className="text-xs text-text-2">
                {t('docs.conflict.description', { version: theirs.current_version })}
              </p>
            </div>
          </header>

          <div className="grid flex-1 gap-3 overflow-auto p-4 md:grid-cols-2">
            <section className="flex min-w-0 flex-col gap-1.5">
              <h3 className="text-2xs tracking-wide text-text-3 uppercase">
                {t('docs.conflict.mine')}
              </h3>
              <pre className="hairline num max-h-[40vh] overflow-auto rounded-[var(--radius-card)] bg-surface-2 p-2 text-2xs whitespace-pre-wrap text-text-1">
                {mine}
              </pre>
            </section>

            <section className="flex min-w-0 flex-col gap-1.5">
              <h3 className="flex flex-wrap items-baseline gap-2 text-2xs tracking-wide text-text-3 uppercase">
                {t('docs.conflict.theirs')}
                <span className="normal-case">{savedBy}</span>
              </h3>
              <pre className="hairline num max-h-[40vh] overflow-auto rounded-[var(--radius-card)] bg-surface-2 p-2 text-2xs whitespace-pre-wrap text-text-1">
                {theirs.content_md ?? ''}
              </pre>
            </section>
          </div>

          <footer className="hairline-t flex flex-wrap items-center justify-end gap-2 px-4 py-3">
            <Button onClick={onDiscardMine} disabled={saving}>
              {t('docs.conflict.discard')}
            </Button>
            <Button variant="primary" onClick={onKeepMine} disabled={saving}>
              {saving ? t('common.state.saving') : t('docs.conflict.keepMine')}
            </Button>
          </footer>
        </div>
      </div>
    </Portal>
  )
}
